import type { ReactNode } from 'react';
import { Card } from 'antd';
import type { FaultToleranceFormState, StepTypePolicyFormState } from './agentFormTypes';
import './FaultToleranceView.css';

/** 秒 → 就近的可整除单位（小时 / 分钟 / 秒） */
function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '-';
  if (seconds % 3600 === 0) return `${seconds / 3600} 小时`;
  if (seconds % 60 === 0) return `${seconds / 60} 分钟`;
  return `${seconds} 秒`;
}

/** 只读状态开关：沿用编辑页 FaultToleranceFields 滑动开关的禁用态样式（半透明 + 禁用光标），仅展示开闭状态 */
function StateSwitch({ on }: { on: boolean }) {
  return (
    <span
      role="switch"
      aria-checked={on}
      aria-disabled="true"
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-not-allowed rounded-full border-2 border-transparent opacity-50 transition-colors duration-200 ease-in-out ${
        on ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
          on ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </span>
  );
}

/** 竖排 label / value 字段（复刻原型 Form vertical item） */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="ft-field-label">{label}</div>
      <div className="ft-field-value">{value}</div>
    </div>
  );
}

/** 单个步骤类型策略（只读） */
function StepPolicyCard({
  label,
  policy,
  isModel,
}: {
  label: string;
  policy: StepTypePolicyFormState;
  isModel?: boolean;
}) {
  return (
    <Card size="small" className="ft-step-card" title={label}>
      <div className="ft-grid">
        <Field label="单次步骤超时" value={formatDuration(policy.attemptTimeoutSeconds)} />
        <Field label="步骤自动重试" value={policy.retryEnabled ? '开启' : '关闭'} />
        {policy.retryEnabled && <Field label="最大重试次数" value={policy.maxRetries} />}
        {policy.retryEnabled && (
          <Field label="重试间隔" value={`${policy.retryIntervalSeconds} 秒`} />
        )}
      </div>
      {isModel && (
        <div className="ft-failover">
          <div className="ft-grid">
            <Field label="模型降级切换" value={policy.failoverEnabled ? '开启' : '关闭'} />
          </div>
          {policy.failoverEnabled && (
            <div style={{ marginTop: 12 }}>
              <div className="ft-field-label">备用模型与请求顺序</div>
              {policy.backupModels.length > 0 ? (
                <div className="ft-models">
                  {policy.backupModels.map((m, i) => (
                    <span key={m} className="ft-model-tag">
                      {i + 1}. {m}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="ft-field-value">未设置</div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

interface Props {
  value: FaultToleranceFormState;
  /** 是否展示「开放给实例单独配置」（模板侧） */
  showConfigurable?: boolean;
  /** 头部附加说明（如实例侧「继承自模板 / 已覆盖」提示） */
  header?: ReactNode;
}

/** 容错配置只读展示，供 Agent 模板详情与实例详情共用；样式对齐开放容错原型 */
export default function FaultToleranceView({ value, showConfigurable, header }: Props) {
  const steps = [
    value.model.selected && { key: 'model', label: '模型调用', policy: value.model, isModel: true },
    value.tool.selected && { key: 'tool', label: '工具调用', policy: value.tool },
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    policy: StepTypePolicyFormState;
    isModel?: boolean;
  }>;

  return (
    <div className="ft-view">
      {header}

      {/* 容错总开关 */}
      <div className="ft-master-row">
        <div>
          <div className="ft-master-title">启用容错配置</div>
          <div className="ft-help">
            关闭后不执行自定义任务重试、步骤重试和模型 Failover；平台基础容错仍生效。
          </div>
        </div>
        <StateSwitch on={value.enabled} />
      </div>

      {!value.enabled ? (
        <div className="ft-empty">未启用容错配置，平台基础容错仍生效。</div>
      ) : (
        <>
          {/* 任务级容错 */}
          <Card
            size="small"
            className="ft-card"
            title="任务级容错"
            extra={<StateSwitch on={value.taskEnabled} />}
          >
            {value.taskEnabled && (
              <div className="ft-grid">
                <Field
                  label="任务总超时时间"
                  value={formatDuration(value.taskTotalTimeoutSeconds)}
                />
                <Field label="任务自动重试" value={value.taskRetryEnabled ? '开启' : '关闭'} />
                {value.taskRetryEnabled && (
                  <Field label="任务最大重试次数" value={value.taskMaxRetries} />
                )}
                {value.taskRetryEnabled && (
                  <Field label="任务重试间隔" value={`${value.taskRetryIntervalSeconds} 秒`} />
                )}
              </div>
            )}
          </Card>

          {/* 步骤级容错 */}
          <Card
            size="small"
            className="ft-card"
            title="步骤级容错"
            extra={<StateSwitch on={value.stepEnabled} />}
          >
            {value.stepEnabled &&
              (steps.length > 0 ? (
                <div className="ft-step-list">
                  {steps.map((s) => (
                    <StepPolicyCard
                      key={s.key}
                      label={s.label}
                      policy={s.policy}
                      isModel={s.isModel}
                    />
                  ))}
                </div>
              ) : (
                <div className="ft-field-value">未选择步骤类型</div>
              ))}
          </Card>

          {/* 开放给实例单独配置 */}
          {showConfigurable && (
            <div className="ft-switch-line">
              <div>
                <div className="ft-label">开放给 Agent 实例单独配置</div>
                <div className="ft-help">
                  开启后，所有关联实例可选择继承模板或覆盖整份容错配置。
                </div>
              </div>
              <StateSwitch on={value.configurable} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
